import { useContext, useEffect } from "react";
import { useLoaderData, useNavigate, useParams, useSearchParams } from "react-router-dom";

import Location from "@r6digital/rapidstor-types/react/entities/Location";
import { MoveInPayload } from "@r6digital/rapidstor-types/types/Events/MoveIn";
import { ReservationEventPayloadStandard } from "@r6digital/rapidstor-types/types/Events/Reservation";

import { useGetReservationQuery, usePostTrackEventMutation } from "@/api/rapidstor";
import Logger from "@/utils/logging";
import { createId } from "@paralleldrive/cuid2";

import { moveInEvent, reservationWithFeeEvent } from "@/features/rapidstor/PostMessageEvents";
import { AnalyticsContext } from "@/features/rapidstor/providers/AnalyticsProvider";

type Type = 'move-in' | 'reservation-with-fee';
type Token = string;

export default function PaymentRedirect() {
    const location = useLoaderData() as Location;
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const { firebaseLogEvent } = useContext(AnalyticsContext);
    const [postTrackEvent] = usePostTrackEventMutation();

    const token = params.token as Token;
    const type = params.type as Type;

    const { data: reservationData, isSuccess: reservationDataLoadedSuccessfully } = useGetReservationQuery({
        locationCode: location.locationCode,
        corpCode: location.corpCode,
        token: token,
    });

    const rsV2Container = document.getElementById('rapidstor-v2-frontend');
    const spinner = document.getElementById('spinner');
    if (spinner && rsV2Container) {
        // rsV2Container.style.display = 'none';
        // spinner.style.display = 'block';
    }

    /**
     * Build move-in event payload
     * 
     * @returns MoveInPayload | null
     */
    const buildMoveInEventPayload = (): MoveInPayload | null => {
        if (!reservationData || !reservationData.unitType) {
            return null;
        }
        const unitTypeRate = (reservationData?.rates.discounted ? reservationData?.rates.discountedRate : reservationData?.rates.rate) || 0;

        return {
            deal_name: `${reservationData?.deal?.title}`,
            size: reservationData?.unitType.area,
            tenant: reservationData?.moveInContext?.tenant ?? {},
            unit_id: reservationData?.UnitID,
            unit_length: reservationData?.unitType.length,
            unit_width: reservationData?.unitType.width,
            unit_name: reservationData?.sUnitName,
            unit_rate: unitTypeRate,
            value: unitTypeRate,
            ledger_id: '',
            quoted_rate_monthly: location.rapidstor.showWeeklyRates ? unitTypeRate * 4 : unitTypeRate,
            quoted_rate_weekly: location.rapidstor.showWeeklyRates ? unitTypeRate : unitTypeRate / 4,
            movein: {
                insuranceType: reservationData.moveInContext?.movein?.insuranceType || null,
                insuranceCoverage: reservationData.moveInContext?.movein?.insuranceCoverage || '',
                fileUploaded: false,
            },
            site_info: {
                location_code: location.locationCode,
                site_token: window.rapidstor_settings.rapidstor_options.locationToken || '',
            },
            reservation: reservationData
        }
    }

    /**
     * Build reservation-with-fee event payload
     * 
     * @returns ReservationEventPayloadStandard | null
     */
    const buildReservationWithFeeEventPayload = (): ReservationEventPayloadStandard | null => {
        if (!reservationData || !reservationData.unitType) {
            return null;
        }
        const unitTypeRate = (reservationData?.rates.discounted ? reservationData?.rates.discountedRate : reservationData?.rates.rate) || 0;

        return {
            quoted_rate_monthly: location.rapidstor.showWeeklyRates ? unitTypeRate * 4 : unitTypeRate,
            quoted_rate_weekly: location.rapidstor.showWeeklyRates ? unitTypeRate : unitTypeRate / 4,
            size: reservationData?.unitType.area,
            unit_id: reservationData?.UnitID,
            unit_length: reservationData?.unitType.length,
            unit_name: reservationData?.sUnitName,
            unit_rate: unitTypeRate,
            unit_width: reservationData?.unitType.width,
            site_info: {
                location_code: location.locationCode,
                site_token: window.rapidstor_settings.rapidstor_options.locationToken || '',
            },
            tenant: {
                firstName: reservationData?.firstName,
                lastName: reservationData?.lastName,
                email: reservationData?.email,
                phone: reservationData?.phone,
                tenantId: reservationData.tenantId,
            },
            value: unitTypeRate,
            reservation: reservationData,
            deal_name: `${reservationData?.deal?.title}`,
        };
    }

    useEffect(() => {
        const handleRedirect = () => {
            if (!token || !type) {
                Logger.warn('[PaymentRedirect] Missing token or type', undefined, [
                    {
                        name: 'token',
                        value: token,
                    },
                    {
                        name: 'type',
                        value: type,
                    },
                ]);
                navigate('/');
            }

            switch (type) {
                case 'move-in': {
                    const moveInPayload = buildMoveInEventPayload();
                    if (!moveInPayload) {
                        Logger.warn('[PaymentRedirect] Missing move-in payload');
                        return;
                    }
                    // Frontend Tracking Listener
                    moveInEvent(moveInPayload);
                    // RapidStor Backend Tracking For Reports
                    postTrackEvent({
                        locationToken: window.rapidstor_settings.rapidstor_options.locationToken,
                        corpToken: window.rapidstor_settings.rapidstor_options.corpToken,
                        payload: {
                            trackingId: createId(),
                            url: window.location.href,
                            referrerUrl: document.referrer,
                            userAgent: window.navigator.userAgent,
                            action: 'move-in',
                            timestamp: new Date().toISOString(),
                        },
                    })

                    /**
                     * R6Digital JBL Wide Analytics Events
                     */
                    firebaseLogEvent('move_in_success', {});
                    firebaseLogEvent('purchase', {
                        currency: location.rapidstor.currency,
                        value: `${moveInPayload.value}`,
                    });

                    if (searchParams.get('error') !== 'true') {
                        navigate(`/move-in-confirmed/${token}`);
                    } else {
                        navigate(`/move-in/${token}?error=true&message=${searchParams.get('message')}`);
                    }

                    break;
                }
                case 'reservation-with-fee': {
                    const reservationWithFeePayload = buildReservationWithFeeEventPayload();
                    if (!reservationWithFeePayload) {
                        Logger.warn('[PaymentRedirect] Missing reservation-with-fee payload');
                        return;
                    }
                    // Frontend Tracking Listener
                    reservationWithFeeEvent(reservationWithFeePayload);
                    // RapidStor Backend Tracking For Reports
                    postTrackEvent({
                        locationToken: window.rapidstor_settings.rapidstor_options.locationToken,
                        corpToken: window.rapidstor_settings.rapidstor_options.corpToken,
                        payload: {
                            trackingId: createId(),
                            url: window.location.href,
                            referrerUrl: document.referrer,
                            userAgent: window.navigator.userAgent,
                            action: 'reservation-with-fee',
                            timestamp: new Date().toISOString(),
                        },
                    });

                    if (searchParams.get('error') !== 'true') {
                        navigate(`/reservation-with-fee/${token}/return`);
                    } else {
                        navigate(`/reservation-with-fee/${token}?error=true&message=${searchParams.get('message')}`);
                    }
                    break;
                }
                default:
                    Logger.warn('[PaymentRedirect] Invalid type', undefined, [
                        {
                            name: 'type',
                            value: type,
                        }
                    ]);
                    navigate('/');
                    break;
            }
        }

        if (reservationDataLoadedSuccessfully) {
            handleRedirect();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservationDataLoadedSuccessfully]);

    return null;
}